import { type Vec2, cellCentre, placeStairs } from '@d2/core';

import { type DrawnFloor, type FloorMismatch, type LevelDescriptor, buildCave, regrowFloor, sameFloor } from './cave';
import { type CharacterFeed, createCharacterFeed } from './character-feed';
import { createMover } from './navigation';
import { NO_INTENT_ACKNOWLEDGED } from './prediction';
import { type RoomLink } from './room-link';
import { createSession, stateOf } from './session';
import { type ConnectedWorldView, createWorldView } from './world-view';

/**
 * Everything between "a room answered" and "a scene can draw": the floor is
 * regrown, checked against the server's checksum **and against its stairs**, and
 * turned into a body, a session and the view a renderer reads.
 *
 * It is here rather than in the React component because it is the part with an
 * *answer* in it — the floor either verifies or it does not, the client either
 * has a body or it takes the entrance — and a decision inside a `useEffect` is
 * a decision nothing can test. `RoomLink` is an interface, so this whole module
 * runs in the unit suite against a stub and never opens a socket.
 *
 * ## The check happens before anything is built, not before anything is drawn
 *
 * `regrowFloor` is the first call, and nothing downstream of it exists unless
 * it succeeded: no mover, no session, no view, no scene. That is deliberate. A
 * client that built the world and *then* checked would have a `Mover` eroded
 * from the wrong grid sitting in a live session, and every subsequent decision —
 * a route, a clearance, a click — would be made against a map the server is not
 * simulating. Refusing early means there is never an object in existence that
 * could draw the wrong cave (`TODO.md` #170).
 *
 * The mover is `regrowFloor`'s own, not one built here beside it. The stairs are
 * a function of the body they are solved for, so the body that answered the
 * check has to be the body the client then walks with — a second one built out
 * here from a different radius would make the check a statement about a body
 * nobody is playing.
 *
 * ## A floor can now change under a live client, and it is checked again
 *
 * A descent rewrites `LevelState`, reseats every player and repopulates the
 * floor inside one message handler (`apps/game`'s `advanceTo`). So the level is
 * no longer something read once at join: it is read off every patch, compared
 * against the floor this client is holding, and — when it has moved — regrown
 * and verified exactly as the first one was.
 *
 * **A floor that fails that second check is refused as hard as the first.** It
 * is not a rarer case than the join, it is a *later* one: two sides using
 * different body radii can agree about floor one's exit by luck and disagree
 * about floor two's, so the descent is precisely where a radius disagreement
 * surfaces. Drawing it anyway would be the client walking toward an exit the
 * server does not have. Nothing is torn down here — this module has no room and
 * no canvas — so the refusal goes up as a value to the caller that has both.
 */

/**
 * The session id the local player has when there is no room to give it one.
 *
 * A constant rather than a random string, because in the renderer sandbox there
 * is exactly one client and nobody to be confused with — and because a stable
 * name is one less thing for the browser suite to have to ask for.
 */
export const SOLO_SESSION_ID = 'solo';

/**
 * Goes nowhere. The renderer sandbox's counterpart to `world-view.ts`'s
 * `unsent`: a client with no room can still press a hotkey, and nobody hears
 * about it.
 */
const UNSENT_INTENTS: CharacterIntents = {
  useSkill: (): void => undefined,
  spendSkillPoint: (): void => undefined,
};

/**
 * The intents a **panel** puts on the wire.
 *
 * Separate from the renderer's `moveTo` because they come from somewhere else: a
 * click on the floor is the scene's, and a cast or a point spend is the DOM
 * overlay's. Both draw their sequence from the one counter every intent shares,
 * because `PlayerState.lastSequence` acknowledges the lot with one field.
 *
 * Neither predicts anything. `ARCHITECTURE.md` allows cosmetic prediction and
 * forbids predicting a damage number, and a cast has nothing *but* a damage
 * number to predict — the mana, the roll and the blow are all the server's, and
 * the client learns what happened from the next snapshot.
 */
export interface CharacterIntents {
  /** "Cast that." `targetId` is the body it lands on, or `null` for a self-cast. */
  readonly useSkill: (skillId: string, targetId: string | null) => void;
  /** "Put a point in that." One click, one point — the wire carries no count. */
  readonly spendSkillPoint: (skillId: string) => void;
}

/** Everything a client needs once a floor has been agreed on. */
export interface StartedWorld {
  /**
   * Where every entity is, where a click goes, **and which floor is being
   * drawn**.
   *
   * The cave is no longer handed out beside this. It used to be, and it was
   * right while a floor was decided once before anybody joined; a descent makes
   * it a value that goes stale, and two copies of "which floor am I on" is
   * exactly the disagreement this milestone is about. One owner, asked once a
   * frame (`world-view.ts`'s `floor`).
   */
  readonly view: ConnectedWorldView;
  /** What this client knows about its own character, from both of the server's channels. */
  readonly feed: CharacterFeed;
  /** Where a cast and a point spend go. */
  readonly intents: CharacterIntents;
  /**
   * A floor the room descended to that this build cannot vouch for.
   *
   * The same refusal the start-up returns, arriving later — so the caller that
   * owns the room and the canvas can do the same thing with it: leave, and say
   * so in words where the canvas would have been. Reported **once**; a refusal
   * is fatal and the level rides every patch, so reporting each one would be
   * twenty identical console lines a second while the client shuts down.
   *
   * Never called at all in the renderer sandbox, which has nobody to disagree
   * with.
   */
  readonly onFloorRefused: (handler: (mismatch: FloorMismatch) => void) => void;
}

export type WorldStart =
  | ({ readonly ok: true } & StartedWorld)
  | { readonly ok: false; readonly mismatch: FloorMismatch };

/**
 * The renderer sandbox: a cave from a seed, a player on its entrance, and
 * nothing to talk to.
 *
 * Every rule it runs is the rule the connected client runs — the same
 * prediction, the same routing, the same fixed step — because it is the same
 * `Session` with no snapshots arriving. It is not a play mode and it is not
 * authoritative about anything; see `launch.ts` for why it exists.
 *
 * It places its **own** stairs, which is the one thing it can honestly do with
 * them: there is no server to agree with, so there is nothing to check and
 * nothing that could be checked. They are drawn and they are clickable, and the
 * descent the click asks for goes nowhere at all — which is what every other
 * intent in this mode does.
 */
export const openSolo = (seed: number): WorldStart => {
  const cave = buildCave(seed);
  const mover = createMover(cave.grid);

  return {
    ok: true,
    view: createWorldView(
      createSession({
        localId: SOLO_SESSION_ID,
        mover,
        spawn: {
          tick: 0,
          position: cellCentre(cave.entrance),
          destination: null,
          lastSequence: NO_INTENT_ACKNOWLEDGED,
        },
      }),
      { cave, stairs: placeStairs(cave.grid, cave.entrance, mover) },
    ),
    // Never fed, exactly as the sandbox's session is never sent a snapshot: a
    // client with nothing to talk to has no character, so the panels are handed
    // a feed that answers "nothing known" and draw nothing rather than being
    // absent from one path and present in the other.
    feed: createCharacterFeed(),
    intents: UNSENT_INTENTS,
    // Nothing to disagree with, so nothing to report. Present rather than
    // absent so a caller wires one path and not two.
    onFloorRefused: (): void => undefined,
  };
};

/**
 * The game: a floor regrown from the room's own numbers, verified, and wired to
 * it in both directions.
 *
 * The local player is seeded from its *own entry* in the snapshot the join was
 * answered with, which is where the server put the body — not from the entrance,
 * even though that is where the room spawns players today. The two agree right
 * now and they stop agreeing the moment a player reconnects to a body that has
 * been standing somewhere else for thirty seconds (`descent.room.ts`,
 * `onDrop`). The entrance is only the fallback for a snapshot that does not
 * mention this client at all, which a room that adds the player during `onJoin`
 * cannot produce.
 */
export const openLinked = (link: RoomLink): WorldStart => {
  const check = regrowFloor(link.level);
  if (!check.ok) return { ok: false, mismatch: check.mismatch };

  const spawn = stateOf(link.initial, link.localId) ?? {
    tick: link.initial.tick,
    position: cellCentre(check.cave.entrance),
    destination: null,
    lastSequence: NO_INTENT_ACKNOWLEDGED,
  };

  // The four intents a *click* can produce, all of them out of the one sequence
  // counter the view holds. They are wired together rather than one at a time
  // because a client that could walk and swing but not pick anything up — or
  // could not leave the floor — is a client nothing would say was broken
  // (`world-view.ts`'s `IntentSinks`).
  const view = createWorldView(
    createSession({ localId: link.localId, mover: check.mover, spawn }),
    { cave: check.cave, stairs: check.stairs },
    {
      moveTo: link.send,
      attack: (targetId: string, sequence: number): void => {
        link.attack({ target: targetId, sequence });
      },
      pickUp: (itemId: string, sequence: number): void => {
        link.pickUp({ item: itemId, sequence });
      },
      descend: (sequence: number): void => {
        link.descend({ sequence });
      },
    },
  );
  const feed = createCharacterFeed();

  /** The floor this client has grown, checked and is drawing. */
  let held: LevelDescriptor = link.level;
  let refused = false;
  let onRefusal: ((mismatch: FloorMismatch) => void) | null = null;

  link.onFloor((level: LevelDescriptor): void => {
    // The level rides every patch, twenty times a second, and is almost always
    // the floor already underfoot. Regrowing on each of them would cost a
    // 128×128 cave a tick and throw the session away with it.
    if (refused || sameFloor(level, held)) return;

    const next = regrowFloor(level);

    if (!next.ok) {
      // Nothing is swapped. The floor on screen is the last one this build could
      // vouch for, and it stays there while the caller shuts the room down —
      // which beats drawing an unverified cave for the seconds that takes.
      refused = true;
      onRefusal?.(next.mismatch);
      return;
    }

    held = level;
    // A different object every time, so a renderer comparing identities can see
    // that the ground moved (`world-view.ts`'s `floor`).
    const arrival: Vec2 = cellCentre(next.cave.entrance);

    // Where the room put every seat when it rebuilt the floor: the entrance,
    // written in the same handler that rewrote the level, so this is known
    // before a snapshot of the new floor has been folded in — and it must be,
    // because that snapshot is the very next thing this patch delivers.
    view.arrive({ cave: next.cave, stairs: next.stairs }, { mover: next.mover, spawn: arrival });
  });

  link.onSnapshot(view.receive);
  link.onCharacter(feed.receiveVitals);
  link.onProgression(feed.receiveProgression);

  return {
    ok: true,
    view,
    feed,
    onFloorRefused: (handler: (mismatch: FloorMismatch) => void): void => {
      onRefusal = handler;
    },
    intents: {
      useSkill: (skillId: string, targetId: string | null): void => {
        // The target is written out even when it is `null`, because the wire's
        // parser insists on the key: "I meant to cast this on myself" and "my
        // client forgot a field" are different messages (`@d2/schema`).
        link.useSkill({ skill: skillId, target: targetId, sequence: view.nextSequence() });
      },
      spendSkillPoint: (skillId: string): void => {
        link.spendSkillPoint({ skill: skillId, sequence: view.nextSequence() });
      },
    },
  };
};

/** Re-exported so a caller that only imports this module can name what it draws. */
export type { DrawnFloor };
